
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {TakipEntity} from "@/entity/TakipEntity";
import {BankaHesabiSahibiLabel} from "@/enum/BankaHesabiSahibi";

import TakipTalebiGenerator from "@/classes/evrak/TakipTalebi/TakipTalebiGenerator";
import FormWrapper from "@/components/FormWrapper.vue";
import Evrak from "@/classes/evrak/Evrak";
import {AlacakKalemiAlani} from "@/enum/AlacakKalemiAlani";
import KapakHesabi from "@/classes/evrak/KapakHesabi/KapakHesabi";
import KapakHesabiCalculator from "@/classes/evrak/KapakHesabi/KapakHesabiCalculator";
import {TakipDurumuLabel, TakipDurumu} from "@/enum/TakipDurumu";
import {HarcTuru} from "@/enum/HarcTuru";
import {ParaBirimi} from "@/enum/ParaBirimi";
import {TahsilatTuru} from "@/enum/TahsilatTuru";
import {
  TaahhutTutarlari,
  kalanTutariHesapla,
  planliGenelToplamHesapla,
  taahhutTutarlariHesapla,
  taksitPlaniVarMi,
} from "@/utils/taahhutTutarlari";
import {TAKSIT_TABLO_TUTAR_ALANLARI} from "@/helpers/taksitTabloHeaders";

/**
 * `takip.taksit_hesap_ayar` iki ayrı biçimde gelebiliyor:
 *   - sunucudan KAYITLI plan     -> JSON metin
 *   - taksitlendirme formundan   -> TaksitHesapAyarEntity NESNESİ (henüz kaydedilmemiş)
 * Eski kod koşulsuz `JSON.parse` ediyordu. Nesne geldiğinde ("[object Object]") ya da
 * alan hiç yokken ("undefined") SyntaxError fırlıyor, bileşen render EDİLEMİYOR ve
 * evrak bomboş açılıyordu (Hüseyin testi, 4. madde). Artık her iki biçim de kabul
 * edilir, çözülemeyen değer null olur ve evrak "plan kaydedilmedi" uyarısını basar.
 */
function hesapAyarCoz(deger: any): any {
  if (deger === null || deger === undefined || deger === "") return null
  if (typeof deger === "object") return deger
  try {
    return JSON.parse(deger as string)
  } catch (e) {
    return null
  }
}

@Component({
  components: {FormWrapper},
})
export default class OdemeTaahhuduTaslak extends Vue {
  @Prop() takip!: TakipEntity;
  @Prop() isKartNoVisible!: boolean;

  kapakHesabi: KapakHesabi = new KapakHesabi();
  ttEvrak: Evrak = new Evrak();
  bankaHesabiSahibiLabel = BankaHesabiSahibiLabel;
  alacakKalemiAlani = AlacakKalemiAlani;
  takipDurumu = TakipDurumuLabel;
  harcTuru = HarcTuru
  odemeTaksitDetaylari:any = []
  isLoadingEvrakData: boolean = false;
  isInternalUpdate: boolean = false;
  ParaBirimi = ParaBirimi
  TahsilatTuru = TahsilatTuru

  hesapAyar = hesapAyarCoz(this.takip?.taksit_hesap_ayar)

  get baroVekaletPuluGoster(): boolean {
    return this.hesapAyar?.baro_vekalet_pulu_gosterilsin_mi !== false
  }
  get vekaletSuretHarciGoster(): boolean {
    return this.hesapAyar?.vekalet_suret_harci_gosterilsin_mi !== false
  }
  get pesinHarcGoster(): boolean {
    return this.hesapAyar?.pesin_harc_gosterilsin_mi !== false
  }

  // TDT-3106: Peşin harç zaten Tahsil Harcı'nın içinde yer aldığından kalem listesinde AYRI
  // bir satır olarak HİÇBİR ZAMAN gösterilmez (elle toplanınca sistemin toplamıyla uyuşmuyordu).
  // Diğer harç/pul kalemleri ise yalnızca ilgili "gösterilsin mi?" ayarı kapalıyken gizlenir.
  kalemGorunsunMu(kalem: any): boolean {
    if (kalem['harc_turu'] === 'Peşin Harç') return false
    if (!this.vekaletSuretHarciGoster && kalem['harc_turu'] === 'Vekalet Suret Harcı') return false
    if (!this.baroVekaletPuluGoster && kalem['masraf_cesidi'] === 'Baro Vekalet Pulu') return false
    return true
  }

  // TDT-3079: Bir kalemin taahhüt toplamından (Genel Toplam / Kalan) düşülüp düşülmeyeceği.
  // Görünürlükten BAĞIMSIZDIR: peşin harç satırı her zaman gizli olsa da, toplamdan yalnızca
  // "peşin harç gösterilsin mi?" KAPALIYKEN düşülür. Varsayılanda (açık) peşin harç tahsil
  // harcının içinde kalır ve toplamdan düşülmez.
  kalemToplamdanDusulsunMu(kalem: any): boolean {
    if (!this.pesinHarcGoster && kalem['harc_turu'] === 'Peşin Harç') return true
    if (!this.vekaletSuretHarciGoster && kalem['harc_turu'] === 'Vekalet Suret Harcı') return true
    if (!this.baroVekaletPuluGoster && kalem['masraf_cesidi'] === 'Baro Vekalet Pulu') return true
    return false
  }

  // TDT-3079: Peşin harç gösterilmiyorsa Tahsil Harcı tutarı, peşin harç düşülmüş olarak yazdırılır.
  // Negatif tutar oluşmaması için 0 ile alt sınırlanır.
  kalemTutariStr(kalem: any): string {
    if (kalem['harc_turu'] === 'Tahsil Harcı' && !this.pesinHarcGoster) {
      return Math.max((Number(kalem['tutar']) || 0) - this.pesinHarc, 0).toFixed(2)
    }
    return kalem['tutar']
  }

  // Evrakta basılan Genel Toplam / işleyecek faiz / taahhüte esas borç üçlüsü.
  // Taksit planı varken üçü de AYNI taksit satırlarından türetilir — aşağıdaki
  // taksit tablosunun kaynağıyla birebir aynı veri. Böylece "Genel Toplam + faiz =
  // taahhüte esas borç" kuruşu kuruşuna tutar ve kaydedilmiş özetin bayatlama riski kalmaz.
  // Ayrıntı ve gerekçe: src/utils/taahhutTutarlari.ts
  get taahhutTutarlari(): TaahhutTutarlari {
    return taahhutTutarlariHesapla(this.hesapAyar, this.odemeTaksitDetaylari, {
      genelToplam: (this.kapakHesabi.genelToplam.get(ParaBirimi.TRY) || 0) - this.gizlenenKalemlerToplami,
      // Plan yokken eski evrak davranışı birebir korunur.
      taahhutBorcu: (this.kapakHesabi.netKalan.get(ParaBirimi.TRY) || 0) + this.kapakHesabi.odemeTaahhuduToplamFaiz,
    })
  }

  // TL'de sunucu tabanı esas alınır; dövizli satırlar istemci hesabıyla basılmaya devam eder
  // (taksit planı yalnızca TL üzerinden kurulur).
  genelToplamTutari(paraBirimi: any): number {
    if (paraBirimi.id === ParaBirimi.TRY) {
      // TDT-3234 (28.09): plan tabanı NET (tahsil edilenler düşülmüş) — üstüne takip sonrası
      // tahsilat eklenir ki "Kalan = Genel Toplam − Tahsilat" tahsilatı bir kez düşsün ve
      // planın ödettiği borca eşit çıksın (bkz. planliGenelToplamHesapla). Tahsilatsız
      // dosyada değişiklik yok.
      if (taksitPlaniVarMi(this.hesapAyar)) {
        return planliGenelToplamHesapla(this.taahhutTutarlari.genelToplam, this.takipSonrasiTahsilatTutari(paraBirimi))
      }
      return this.taahhutTutarlari.genelToplam
    }
    return this.kapakHesabi.genelToplam.get(paraBirimi.id) || 0
  }

  // Takip sonrası tahsilat — "Kalan" satırının tek çıkarma terimi.
  // KapakHesabi.takipSonrasiTahsilatData'da o para birimi için kayıt olmayabilir
  // (hiç tahsilat yoksa); `|| 0` olmadan Number(undefined) = NaN üretir.
  takipSonrasiTahsilatTutari(paraBirimi: any): number {
    return Number(this.kapakHesabi.takipSonrasiTahsilatData.get(paraBirimi.id)) || 0
  }

  // "Kalan" = Genel Toplam − Takip Sonrası Tahsilat. (Hüseyin testi, 1. madde.)
  //
  // Eskiden bu satır kapakHesabi.netKalan'dan besleniyordu; Genel Toplam ise taksit
  // planı varken taksit satırlarından türetiliyor (bkz. taahhutTutarlari). İki ayrı
  // kaynak olduğu için satırlar birbirini tutmuyordu. Üstelik netKalan tahsilatsız
  // dosyada NaN geliyor, $helper.tutarStr'deki `if (!val) val = 0` onu sessizce
  // 0,00 TL'ye çeviriyordu — sahada görülen "Kalan 0,00 TL" tam olarak buydu.
  // Artık Genel Toplam hangi kaynaktan geliyorsa Kalan da aynısından türer.
  kalanTutari(paraBirimi: any): number {
    return kalanTutariHesapla(
      this.genelToplamTutari(paraBirimi),
      this.takipSonrasiTahsilatTutari(paraBirimi),
    )
  }

  // Evrakın altındaki "HESAP TARİHİ: … TAAHHÜT TARİHİ İTİBARİYLE DOSYA KAPAK HESABI"
  // cümlesi, kapak hesabının HANGİ TARİHE göre kurulduğunu yazmalı. (Hüseyin testi,
  // 2. madde.) Eskiden takip.takip_tarihi — takibin AÇILIŞ tarihi — basılıyordu;
  // hesap doğru, yazılan tarih yanlıştı. Kaynak: kullanıcının seçtiği hesap tarihi
  // (takipDetay store, HesapTarihiForm ile ayarlanır), seçilmemişse bugün — kartın
  // "Hesap Tarihi" alanıyla aynı semantik.
  get hesapTarihi(): Date {
    const secilen = this.$store.state.takipDetay?.hesapTarihi
    return secilen ? new Date(secilen) : new Date()
  }

  // Evrak yalnızca KAYITLI plandan beslenir: handleTaksitler() GET .../taksit ile
  // sunucudaki satırları çeker, hesaplanıp kaydedilmemiş satırlar orada yoktur.
  get planKayitliMi(): boolean {
    return (this.odemeTaksitDetaylari?.length || 0) > 0
  }

  get gizlenenKalemlerToplami(): number {
    let toplam = 0
    this.kapakHesabi.duzenlenmisTakipSonrasiKalemler.forEach((kalem: any) => {
      if (this.kalemToplamdanDusulsunMu(kalem)) {
        // Her kalem KENDİ tutarıyla bir kez düşülür. Çok-alacaklı dosyada birden fazla
        // peşin harç satırı olabilir; her biri kendi tutarını katar (tek bir scalar değil).
        toplam += Number(kalem['tutar']) || 0
      }
    })
    return toplam
  }

  // TDT-3105: Takip sonrası işlemiş faiz kademeli (birden fazla alani=17 kalemi) olabilir.
  // Taahhütte her kademeyi ayrı yazmak yerine PARA BİRİMİ bazında toplam işlemiş faizi
  // tek satırda göstermek için. (Döviz takiplerde kademeler kursuz toplanmaz, ayrı satır.)
  get takipSonrasiIslemisFaizSatirlari(): Array<{ paraBirimi: string, toplam: number }> {
    const map = new Map<string, number>()
    this.kapakHesabi.duzenlenmisTakipSonrasiKalemler.forEach((kalem: any) => {
      if (Number(kalem['id']) === this.alacakKalemiAlani.takipSonrasiFaiz) {
        const pb = kalem['paraBirimi'] || 'TL'
        map.set(pb, (map.get(pb) || 0) + (Number(kalem['tutar']) || 0))
      }
    })
    return Array.from(map.entries()).map(([paraBirimi, toplam]) => ({ paraBirimi, toplam }))
  }

  // TDT-3107: Taahhüdü hangi borçlu(lar)ın verdiği taksit_hesap_ayar içinde seçilebilir.
  // Seçim yoksa (boş) tüm borçlular gösterilir; dosyada tek borçlu varsa zaten o gösterilir.
  get secilenBorcluIdleri(): Array<number> {
    const ids = this.hesapAyar?.secilen_borclu_idleri
    return Array.isArray(ids) ? ids.map((i: any) => Number(i)) : []
  }

  borcluSecili(borcluData: any): boolean {
    if (!this.secilenBorcluIdleri.length) return true
    return this.secilenBorcluIdleri.includes(Number(borcluData?.id))
  }

  // Taksit tablosu sütun genişlikleri YÜZDE olarak verilir ve toplamları 100 olmak
  // ZORUNDADIR. Word, HTML tabloyu sayfa genişliğine göre otomatik daraltmaz: genişlik
  // bildirilmezse tabloyu içerik genişliğinde bırakıp sağ marjinden taşırır. Yüzdeler
  // hem <colgroup> hem de hücrelerin width özniteliğine basılır (Word HTML içe
  // aktarıcısı table-layout:fixed'i yok sayar, width özniteliğini dikkate alır).
  // Değişmezi `tests/unit/components/odemeTaahhuduTaksitTablosu.spec.ts` doğrular.
  //
  // Kolonlar DEV-2300 klasik taahhüt belgesi düzenidir ve sıraları
  // TAKSIT_TABLO_HEADERS (src/helpers/taksitTabloHeaders.ts) ile AYNI olmalıdır;
  // `odemeTaahhuduKolonSirasi.spec.ts` ikisinin ayrışmasını yakalar. Dizi satır içi
  // yazılır çünkü genişlik testi SFC kaynağından bu literal'i okuyup çalıştırır.
  headers = [
    {text: 'Taksit No', value: 'index', width: '7%'},
    {text: 'Gün', value: 'faiz_isleyen_gun', width: '5%'},
    {text: 'Tarih', value: 'tarih', width: '9%'},
    {text: 'Faiz İşletilecek Tutar', value: 'faiz_isletilecek_tutar', width: '9%'},
    {text: 'İşleyen Faiz', value: 'isleyen_faiz', width: '9%'},
    {text: 'BSMV', value: 'isleyen_faiz_bsmv', width: '7%'},
    {text: 'Toplam Faiz', value: 'toplam_faiz', width: '9%'},
    {text: 'Kalan Ana Para', value: 'kalan_ana_para', width: '9%'},
    {text: 'Toplam Borç', value: 'toplam_borc', width: '9%'},
    {text: 'Kalan Borç', value: 'kalan_borc', width: '9%'},
    {text: 'Tahsil Harcı', value: 'tahsil_harci', width: '9%'},
    {text: 'Taksit Miktarı', value: 'tutar', width: '9%'},
  ]

  taksitHucreGoster(taksit: any, alan: string): string {
    const deger = taksit[alan];
    if (alan === 'tarih') {
      return deger ? this.$helper.tarihStr(deger) : '';
    }
    if (alan === 'index' || alan === 'faiz_isleyen_gun') {
      return deger != null && deger !== '' ? String(deger) : '';
    }
    if (TAKSIT_TABLO_TUTAR_ALANLARI.includes(alan)) {
      return deger == null ? '' : this.$helper.tutarStr(deger);
    }
    return deger != null ? String(deger) : '';
  }

  async handleTaksitler(){
    const takipId = this.takip?.id || this.$route.params.id;
    if (takipId) {
      try {
        const response = await this.$http.get('/api/v1/takip/' + takipId + '/taksit');
        this.odemeTaksitDetaylari = response.data;
      } catch (error) {
        console.error('Taksit verileri yüklenirken hata oluştu:', error);
      }
    }
  }
  get taksitToplamFaizIsleyenGun(){
    let toplam:number = 0
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      toplam = toplam+taksit.faiz_isleyen_gun
    })
    return toplam
  }

  get pesinHarc(){
    let pesinHarc:number = 0
    // Peşin harç tutarını doğrudan alacak kalemlerinden al (Harç butonundaki tablo ile aynı kaynak)
    if (this.takip && this.takip.talepler) {
      this.takip.talepler.forEach((talep: any) => {
        if (talep.alacaklar) {
          talep.alacaklar.forEach((alacak: any) => {
            if (alacak.alacak_kalemleri) {
              alacak.alacak_kalemleri.forEach((kalem: any) => {
                if (kalem.harc_turu_id === this.harcTuru.pesin) {
                  // Çok-alacaklı dosyada birden fazla peşin harç kalemi olabilir; topla.
                  pesinHarc += Number(kalem.tutar)
                }
              })
            }
          })
        }
      })
    }
    return pesinHarc
  }
  get toplamIsleyecekFaiz(){
    let toplam:number = 0.00
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      toplam=toplam+Number(taksit.isleyen_faiz)
    })
    return toplam.toFixed(2)
  }

  get toplamBsmv(){
    let toplam:number = 0.00
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      if (taksit.isleyen_faiz_bsmv != null) {
        toplam += Number(taksit.isleyen_faiz_bsmv)
      }
    })
    return toplam.toFixed(2)
  }

  get toplamTahsilHarci(){
    let toplam:number = 0.00
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      if (taksit.tahsil_harci != null) {
        toplam += Number(taksit.tahsil_harci)
      }
    })
    return toplam.toFixed(2)
  }

  get toplamtaksitMiktari(){
    let toplam:number = 0.00
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      toplam=toplam+Number(taksit.tutar)
    })
    return toplam.toFixed(2)
  }

  async mounted() {
    if (this.takip && this.takip.id) {
      this.isLoadingEvrakData = true;

      try {
        /*// Takip durumuna göre hangi store kullanılacağına karar ver
        const isAcilmamis = this.takip?.takip_durumu === TakipDurumu.acilmamis;

        // İlgili store'dan borçlu verilerini al
        const borclular = isAcilmamis
          ? this.$store.state.takipEkle?.takipBorclular?.data
          : this.$store.state.takipDetay?.takip?.borclular;

        if (borclular && Array.isArray(borclular) && borclular.length > 0) {
          // Takip durumuna göre ilgili store'un action'ını çağır
          const storeName = isAcilmamis ? 'takipEkle' : 'takipDetay';
          await this.$store.dispatch(`${storeName}/loadBorcluVekillerVeTebligatlar`, borclular);

          // State'deki güncel borçlu verilerini takip objesine merge et
          this.mergeBorcluDataFromState();
        }*/

        // Tüm hesaplamaları yap
        this.generateTtEvrak();
        this.calculateKapakHesabi();
        await this.handleTaksitler();
      } catch (error) {
        console.error('Ödeme taahhüdü verileri yüklenirken hata oluştu:', error);
      } finally {
        this.isLoadingEvrakData = false;
      }
    }
  }

  /*mergeBorcluDataFromState() {
    // Takip durumuna göre hangi store'dan veri çekileceğine karar ver
    const isAcilmamis = this.takip?.takip_durumu === TakipDurumu.acilmamis;

    // İlgili store'dan güncellenmiş borçlu verilerini al
    const stateBorclular = isAcilmamis
      ? this.$store.state.takipEkle?.takipBorclular?.data
      : this.$store.state.takipDetay?.takip?.borclular;

    if (!stateBorclular || !this.takip?.borclular) return;

    this.takip.borclular.forEach((takipBorclu: any) => {
      const stateBorclu = stateBorclular.find((sb: any) => sb.id === takipBorclu.id);
      if (stateBorclu) {
        // State'deki yeni field'ları takip objesindeki borçluya ekle
        takipBorclu.vekiller = stateBorclu.vekiller || [];
        takipBorclu.tebligatlar = stateBorclu.tebligatlar || [];
        takipBorclu.borclu_bilgileri = stateBorclu.borclu_bilgileri || null;
        takipBorclu.adresler = stateBorclu.adresler || [];
        takipBorclu.varsayilan_adres = stateBorclu.varsayilan_adres || null;
      }
    });
  }*/

  generateTtEvrak() {
    if (this.takip && this.takip.id) {
      try {
        this.isInternalUpdate = true;

        // Borçlular için eksik alanları kontrol et ve varsayılan değerler ekle
        if (this.takip.borclular && this.takip.borclular.length > 0) {
          this.takip.borclular.forEach((borclu: any) => {
            // borclu_bilgileri yoksa boş obje oluştur
            if (!borclu.borclu_bilgileri) {
              borclu.borclu_bilgileri = {
                yabanci_uyruklu_mu: false,
                tebligat_adresi: null,
                vekiller: [],
                adresler: []
              };
            }
            // yabanci_uyruklu_mu yoksa false yap
            if (borclu.borclu_bilgileri.yabanci_uyruklu_mu === undefined) {
              borclu.borclu_bilgileri.yabanci_uyruklu_mu = false;
            }
          });
        }

        let takipTalebiGenerator = new TakipTalebiGenerator()
        takipTalebiGenerator.setTakip(this.takip);
        this.ttEvrak = takipTalebiGenerator.getTakipTalebiEvrak();

      } catch (error) {
        console.error('TakipTalebiGenerator hatası:', error);
        // Hata durumunda en azından temel bilgileri set et
        this.ttEvrak.icraDairesi = this.takip.icra_dairesi?.isim || '';
        this.ttEvrak.dosyaEsasNo = this.takip.dosya_no || '';
        this.ttEvrak.kartNo = this.takip.id?.toString() || '';
      } finally {
        // Reset flag after mutations complete
        this.$nextTick(() => {
          this.isInternalUpdate = false;
        });
      }
    }
  }

  get dovizTahsilatlar(){
    let tahsilatlar :any = []
    let dovizToplami:number = 0

    this.takip.tahsilatlar.forEach(tah =>{
      tahsilatlar.push(tah)
    })
    tahsilatlar = tahsilatlar.filter((tah:any) => tah.para_birimi_id !== ParaBirimi.TRY);

    tahsilatlar.forEach((doviz:any) => {
      dovizToplami += doviz.tutar * doviz.kur;
    });
    return dovizToplami;
  }

  get tahsilHarcOrani(){
    let tahsilHarcOrani:any
    this.takip.talepler[0].alacaklar.forEach((alacak) => {
      alacak.alacak_kalemleri.forEach((kalemler) => {
        if (kalemler.harc_turu_id === this.harcTuru.tahsil_harci) {
          tahsilHarcOrani=kalemler.harc_orani;
        }
      })
    })
    if (tahsilHarcOrani==null){
      tahsilHarcOrani = "4.55"
    }
    return tahsilHarcOrani

  }

  get cezaEviHarci(){
    let tahsilatlar:any = []

    this.takip.tahsilatlar.forEach((tahsilat:any)=>{
      tahsilatlar ={"cezaevi_harci":tahsilat.cezaevi_harci,
        "tahsilat_turu": tahsilat.tahsilat_turu}
    })
    return tahsilatlar
  }

  calculateKapakHesabi() {
    if (this.takip.id) {
      let kapakHesabiCalculator: KapakHesabiCalculator = new KapakHesabiCalculator();
      kapakHesabiCalculator.setTakip(this.takip);
      this.kapakHesabi = kapakHesabiCalculator.calculateKapakHesabi();
      this.kapakHesabi.alacakKalemiAlanlari.sort(function (a, b) {
        return a - b;
      });
      this.kapakHesabi.takipSonrasialacakKalemiAlanlari.sort(function (a, b) {
        return a - b;
      });
    }
  }

  @Watch("takip", { deep: true })
  onTakipChange() {
    // Prevent infinite loop from internal updates
    if (this.isInternalUpdate) {
      return;
    }

    if (this.takip && this.takip.id) {
      this.generateTtEvrak();
    }
  }

  print() {
    this.$helper.printComponent('odemeTaahhuduTaslak');
  }

  imzasizUdfIndir(){
    this.$helper.getUdfFromComponent("odemeTaahhuduTaslak",true,this.takip.id + "_odemeTaahhuduTaslak");
  }

  imzaliUdfIndir(){
    this.$helper.getImzaliUdfFromComponent("odemeTaahhuduTaslak",true,this.takip.id + "_imzali_odemeTaahhuduTaslak");
  }

}
